import { useCallback, useEffect, useState } from "react";
import * as api from "../../api/client";
import { Alert, Filters, List, Pager, Search, Stat, Stats } from "./ui";
import { TONE, usePaged } from "./lib";

const VERDICT = {
  togri: ["To'g'ri", TONE.ok],
  xato: ["Xato", TONE.bad],
  noaniq: ["Noaniq", "border-amber-800/50 bg-amber-950/50 text-amber-200"],
};

const FILTERS = [
  ["", "Hammasi"],
  ["unrated", "Baholanmagan"],
  ["togri", "To'g'ri"],
  ["xato", "Xato"],
  ["noaniq", "Noaniq"],
];

const PAGE = 25;

/** Suhbat: savol-javoblar, ekspert baholari va tuzatilgan kodlar. */
export default function ChatTab({ days, refresh, preset = "", onChanged }) {
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState(preset);
  const [q, setQ] = useState("");
  const [statsErr, setStatsErr] = useState("");

  const loadStats = useCallback(
    () => api.reviewStats({ days }).then(setStats, (e) => setStatsErr(e.message || String(e))),
    [days],
  );
  useEffect(() => {
    loadStats();
  }, [loadStats, refresh]);

  const list = usePaged(
    (page) =>
      api.reviewTurns({
        limit: PAGE,
        offset: page * PAGE,
        unrated: filter === "unrated",
        verdict: filter === "unrated" ? "" : filter,
        q,
        days,
      }),
    [filter, q, days],
    refresh,
  );

  const rated = () => {
    list.reload();
    loadStats();
    onChanged?.();
  };

  return (
    <div className="space-y-5">
      <Alert>{statsErr || list.error}</Alert>

      {stats && (
        <Stats>
          {/* Faqat baholanganlarga nisbatan; baho yo'q bo'lsa chiziqcha */}
          <Stat
            label="Aniqlik"
            value={stats.accuracy === null ? "—" : `${stats.accuracy}%`}
            hint={`${stats.rated} baholangan`}
            tone={stats.accuracy === null ? "text-sky-400/50" : "text-emerald-300"}
          />
          <Stat label="Savollar" value={stats.turns} hint={`${stats.sessions} sessiya`} />
          <Stat label="Baholanmagan" value={stats.unrated} tone={stats.unrated ? "text-amber-300" : ""} />
          <Stat label="Manbasiz" value={stats.no_tool} />
          <Stat label="Ogohlantirish" value={stats.warned} />
          <Stat label="Xatolar" value={stats.errored} tone={stats.errored ? "text-rose-300" : ""} />
        </Stats>
      )}

      {stats?.corrections?.length > 0 && <Corrections items={stats.corrections} />}

      <Filters items={FILTERS} value={filter} onChange={setFilter}>
        <Search placeholder="Savol yoki kod bo'yicha qidirish..." onSearch={setQ} />
      </Filters>

      <List loading={list.loading} items={list.items}>
        <div className="space-y-3">
          {list.items.map((t) => (
            <TurnCard key={t.id} turn={t} onRated={rated} />
          ))}
        </div>
      </List>

      <Pager page={list.page} setPage={list.setPage} full={list.items.length === PAGE} />
    </div>
  );
}

/** Ekspert tuzatgan kodlar — kaskadni sozlash uchun to'g'ridan-to'g'ri material. */
export function Corrections({ items, limit }) {
  const rows = limit ? items.slice(0, limit) : items;
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-white">Ekspert tuzatgan kodlar ({items.length})</h2>
      <div className="overflow-x-auto rounded-xl border border-sky-800/40">
        <table className="w-full min-w-[520px] text-left text-[11px]">
          <thead className="bg-[#041228] text-sky-400/70">
            <tr>
              <th className="px-3 py-2 font-medium">Savol</th>
              <th className="px-3 py-2 font-medium">Berilgan</th>
              <th className="px-3 py-2 font-medium">To'g'ri</th>
              <th className="px-3 py-2 font-medium">Izoh</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={i} className="border-t border-sky-900/40 bg-[#030f24]/60">
                <td className="max-w-[240px] truncate px-3 py-2 text-slate-200">{c.question}</td>
                <td className="px-3 py-2 font-mono text-rose-300">{c.given || "—"}</td>
                <td className="px-3 py-2 font-mono text-emerald-300">{c.correct}</td>
                <td className="max-w-[220px] truncate px-3 py-2 text-sky-300/70">{c.comment || c.issue || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TurnCard({ turn, onRated }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(false);
  const [correct, setCorrect] = useState("");
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");

  const rate = async (verdict) => {
    setErr("");
    try {
      await api.sendFeedback({
        turn_id: turn.id,
        verdict,
        correct_code: correct.trim(),
        comment: comment.trim(),
      });
      setRating(false);
      setCorrect("");
      setComment("");
      onRated();
    } catch (e) {
      setErr(e.message || String(e));
    }
  };

  return (
    <div className="rounded-xl border border-sky-800/40 bg-[#030f24]/70 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {turn.code && (
              <span className="rounded border border-sky-800/50 bg-sky-950/80 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-sky-200">
                {turn.code}
              </span>
            )}
            {/* Tool chaqirilmagan javob — model xotiradan gapirgan. */}
            {turn.tools.length === 0 && (
              <span className="rounded border border-rose-800/50 bg-rose-950/50 px-1.5 py-0.5 text-[10px] text-rose-300">
                manbasiz
              </span>
            )}
            {turn.warning && (
              <span className="rounded border border-amber-800/50 bg-amber-950/50 px-1.5 py-0.5 text-[10px] text-amber-300">
                ogohlantirish
              </span>
            )}
            {turn.feedback.map((f) => {
              const [label, cls] = VERDICT[f.verdict] ?? [f.verdict, ""];
              return (
                <span
                  key={f.id}
                  className={`rounded border px-1.5 py-0.5 text-[10px] ${cls}`}
                >
                  {label}
                  {f.correct_code ? ` → ${f.correct_code}` : ""}
                </span>
              );
            })}
          </div>
          <div className="mt-1.5 text-xs font-medium text-white">
            {turn.question}
          </div>
        </div>
        <div className="shrink-0 text-right font-mono text-[10px] text-sky-400/50">
          <div>{new Date(turn.asked_at).toLocaleString("uz-UZ")}</div>
          {turn.total_ms > 0 && <div>{(turn.total_ms / 1000).toFixed(1)}s</div>}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] text-sky-300 transition-colors hover:text-white"
        >
          {open ? "Javobni yashirish" : "Javobni ko'rish"}
        </button>
        {!rating && (
          <button
            onClick={() => setRating(true)}
            className="text-[11px] text-sky-300 transition-colors hover:text-white"
          >
            Baho qo'shish
          </button>
        )}
        {turn.session_id && (
          <a
            href={`/?chat=${encodeURIComponent(turn.session_id)}`}
            className="text-[11px] text-sky-300 transition-colors hover:text-white"
          >
            Suhbatni ochish
          </a>
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          <div className="whitespace-pre-wrap rounded-lg border border-sky-900/50 bg-[#041228]/80 p-2.5 text-[11px] leading-relaxed text-slate-300">
            {turn.answer || "(javob bo'sh)"}
          </div>
          {turn.feedback.map((f) =>
            f.comment ? (
              <div
                key={f.id}
                className="rounded-lg border border-sky-900/50 bg-[#041228]/60 p-2 text-[11px] text-sky-200"
              >
                <span className="text-sky-400/60">{f.author || "ekspert"}:</span>{" "}
                {f.comment}
              </div>
            ) : null,
          )}
        </div>
      )}

      {rating && (
        <div className="mt-2 space-y-2 rounded-lg border border-sky-800/40 bg-[#041228]/80 p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={correct}
              onChange={(e) => setCorrect(e.target.value)}
              placeholder="To'g'ri kod"
              inputMode="numeric"
              className="w-28 rounded-lg border border-sky-800/50 bg-[#030f24] px-2 py-1 font-mono text-[11px] text-white placeholder:text-sky-700 focus:border-sky-500 focus:outline-none"
            />
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Izoh"
              className="min-w-0 flex-1 rounded-lg border border-sky-800/50 bg-[#030f24] px-2 py-1 text-[11px] text-white placeholder:text-sky-700 focus:border-sky-500 focus:outline-none"
            />
          </div>
          {err && <div className="text-[11px] text-rose-300">{err}</div>}
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => rate("togri")}
              className="rounded-lg border border-emerald-800/50 bg-emerald-950/50 px-2.5 py-1 text-[11px] text-emerald-200 hover:bg-emerald-900/50"
            >
              To'g'ri
            </button>
            <button
              onClick={() => rate("xato")}
              className="rounded-lg border border-rose-800/50 bg-rose-950/50 px-2.5 py-1 text-[11px] text-rose-200 hover:bg-rose-900/50"
            >
              Xato
            </button>
            <button
              onClick={() => rate("noaniq")}
              className="rounded-lg border border-amber-800/50 bg-amber-950/50 px-2.5 py-1 text-[11px] text-amber-200 hover:bg-amber-900/50"
            >
              Noaniq
            </button>
            <button
              onClick={() => setRating(false)}
              className="rounded-lg px-2 py-1 text-[11px] text-sky-400/70 hover:text-sky-200"
            >
              Bekor
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
